import { Fragment, useState, useMemo, useEffect, useRef } from 'react'
import '../styles/Sidebar.css'
import MilitaryPanel from './MilitaryPanel'
import Icon from './Icons'
import { OWN_LAYERS, buildLoginUrl } from '../utils/regionStats'
import { getCountryFlag } from '../utils/flagCache'
import { pathLengthKm, housingCost, housingComfort } from '../utils/structures'
import { BUBBLE_METRICS } from './MapView/useBubbleMap'
import BookmarksSection from './BookmarksSection'
import InfraToolPanel from './InfraToolPanel'
import { loadCities, searchRegions } from '../utils/regionSearch'
import { countryLabel } from '../utils/countryLabel'
import { usePrefsStore } from '../store/prefsStore'

const SectionTitle = ({ icon, children }) => (
  <h2><Icon name={icon} />{children}</h2>
)

// Reorderable + collapsible "view" sections — order and collapsed state
// persist to localStorage so power users can e.g. put Frontières above
// Visualisation and it sticks. The heavier "mode" sections (Plan Mode,
// Planification militaire, Commandement, Construction, Règle) stay fixed —
// they're stateful tool panels, not view layers, and reordering them adds
// risk with little payoff.
const SECTION_ORDER_KEY = 'onlinemap-sidebar-section-order'
const SECTION_COLLAPSED_KEY = 'onlinemap-sidebar-section-collapsed'
const DEFAULT_SECTION_ORDER = ['vue', 'signets', 'visualisation', 'bulles', 'elements-rp', 'monde', 'organisations', 'projection', 'filtres', 'stats']

const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

const SidebarSection = ({
  id, icon, title, extra, collapsed, onToggleCollapsed,
  onDragStart, onDragOver, onDrop, onDragEnd, dragOver, children,
}) => (
  <div
    className={`sidebar-section draggable-section ${dragOver ? 'drag-over' : ''}`}
    onDragOver={onDragOver}
    onDrop={onDrop}
  >
    <h2
      className="section-title-toggle"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      role="button"
      tabIndex={0}
      aria-expanded={!collapsed}
      onClick={onToggleCollapsed}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleCollapsed() }
      }}
      title={`Glisser pour réordonner « ${title} »`}
    >
      <Icon name="menu" size={12} className="drag-grip" />
      <Icon name={icon} />
      <span className="section-title-label">{title}</span>
      {extra}
      <Icon name={collapsed ? 'chevronDown' : 'chevronUp'} size={14} />
    </h2>
    {!collapsed && children}
  </div>
)

// Bottom-sheet drag bounds (mobile only)
const SHEET_MIN_BASE = 64 // collapsed: just the handle bar
const SHEET_MIN_CAP = 140 // sanity ceiling in case a device reports a wild safe-area value
const SHEET_MAX_RATIO = 1 // can cover the whole map if pulled all the way up
const SHEET_DEFAULT_RATIO = 0.55
const FLING_VELOCITY = 1.3 // px/ms — drag released above this speed snaps instead of locking in place
const FLING_MIN_DISTANCE = 30 // px — also require a real downward swipe, not just a velocity blip
const SNAP_TOP_THRESHOLD = 40 // px from true full height — dragging into this zone "hooks" shut to 100%, like a shutter

// Real safe-area-inset-bottom (home indicator / gesture bar), not a guess — read
// from the CSS env() value via the custom property set in index.css. Without
// this, the handle's own content (grip+label, pushed up by its own safe-area
// padding) can be taller than a hardcoded collapsed height, clipping it out of
// view on devices with a real inset.
const getSheetMin = () => {
  if (typeof document === 'undefined') return SHEET_MIN_BASE
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-bottom')
  const inset = parseFloat(raw)
  if (!Number.isFinite(inset) || inset < 0) return SHEET_MIN_BASE
  return Math.min(SHEET_MIN_BASE + inset, SHEET_MIN_CAP)
}

// visualViewport tracks the actually-visible area (excludes on-screen keyboard,
// more reliable than innerHeight on some mobile browsers when the toolbar is
// transitioning show/hide mid-gesture).
const getViewportHeight = () => {
  if (typeof window === 'undefined') return 0
  return window.visualViewport?.height || window.innerHeight
}

const Sidebar = ({
  layers,
  filters,
  onToggleLayer,
  onUpdateFilter,
  activeCountriesCount,
  planMode,
  onTogglePlanMode,
  selectedColor,
  onColorChange,
  selectionMode,
  onSelectionModeChange,
  onClearAll,
  onExportPNG,
  onExportJSON,
  onImportJSON,
  coloredRegionsCount,
  isGlobeView,
  onToggleGlobeView,
  satelliteMode,
  onSetSatelliteMode,
  borderMode,
  onBorderModeChange,
  borderWidth,
  onBorderWidthChange,
  borderColor,
  onBorderColorChange,
  borderOpacity,
  onBorderOpacityChange,
  borderStyle,
  onBorderStyleChange,
  showBubbles,
  onToggleShowBubbles,
  bubbleMetric,
  onBubbleMetricChange,
  onAssembleState,
  onApplyState,
  onShareView,
  onExportImage,
  onExportSession,
  regionsData,
  weatherData,
  onCenterMap,
  hasOwnedData,
  ownedCountryId,
  statMeta,
  statType,
  onStatTypeChange,
  structOptions,
  structFilter,
  onStructFilterChange,
  showStructures,
  onToggleShowStructures,
  showInfrastructure,
  onToggleShowInfrastructure,
  showMilitary,
  onToggleShowMilitary,
  showCities,
  onToggleShowCities,
  showRoads,
  onToggleShowRoads,
  showRailways,
  onToggleShowRailways,
  showHydrography,
  onToggleShowHydrography,
  overlayStyles,
  onUpdateOverlayStyle,
  onResetOverlayStyle,
  onUpdateInfraStyle,
  onResetInfraStyle,
  infrastructureData,
  militaryPlanning,
  onToggleMilitaryPlanning,
  military,
  constructionMode,
  onToggleConstructionMode,
  constructionTool,
  onConstructionToolChange,
  structureTypeData,
  powerPlantData,
  housingOptions,
  constructionSelection,
  onConstructionSelectionChange,
  constructionStatus,
  skipConstructionConfirm,
  onToggleSkipConstructionConfirm,
  notifySecret,
  onToggleNotifySecret,
  hasSecretChannel,
  infraTypes,
  infrastructurePath,
  infrastructureSelection,
  onInfrastructureSelectionChange,
  onFinishInfrastructure,
  onRestartInfrastructure,
  infraMode,
  onInfraModeChange,
  selectedTroncIds,
  onTroncSelect,
  onTroncClearSelection,
  onTroncRework,
  onTroncReset,
  onTroncMerge,
  onUndoInfraPoint,
  onCancelInfrastructure,
  editingInfra,
  rulerMode,
  onToggleRulerMode,
  rulerPath,
  onUndoRulerPoint,
  onClearRuler,
  rulerCrowFlies,
  onRulerCrowFliesChange,
  rangeMode,
  onToggleRangeMode,
  rangeCenter,
  rangeRadiusKm,
  onRangeRadiusChange,
  onClearRange,
  organizationsCatalog,
  selectedOrgId,
  wars,
  selectedWarId,
  onSelectWar,
  onSelectOrg,
  showToast
}) => {
  const hiddenSections = usePrefsStore((s) => s.hiddenSections)
  const hiddenSectionSet = useMemo(() => new Set(hiddenSections || []), [hiddenSections])

  const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth <= 768
  const defaultExpandedHeight = () =>
    typeof window !== 'undefined' ? Math.round(getViewportHeight() * SHEET_DEFAULT_RATIO) : 400

  const [isCollapsed, setIsCollapsed] = useState(() => isMobileViewport())

  // Bottom-sheet free-drag height (mobile only — desktop ignores this var).
  // Always kept in sync with isCollapsed (collapsed === sheet min) so a drag
  // never starts from a stale/mismatched baseline.
  const [sheetHeight, setSheetHeight] = useState(() =>
    isMobileViewport() ? getSheetMin() : defaultExpandedHeight()
  )
  const [isDragging, setIsDragging] = useState(false)
  const dragStateRef = useRef({ active: false, startY: 0, startHeight: 0, moved: false, lastY: 0, lastT: 0, velocity: 0 })
  const lastExpandedHeightRef = useRef(defaultExpandedHeight())

  const maxSheetHeight = () => getViewportHeight() * SHEET_MAX_RATIO

  const clampSheetHeight = (h) => {
    if (!Number.isFinite(h)) return sheetHeight // bad input (NaN from a malformed event) — hold position, don't collapse
    const max = maxSheetHeight()
    const min = getSheetMin()
    // Magnetic top zone: once dragged within SNAP_TOP_THRESHOLD of true full
    // height, hook straight to 100% — a "shutter" that closes the rest of the
    // way on its own instead of requiring pixel-perfect dragging to the edge.
    if (h >= max - SNAP_TOP_THRESHOLD) return max
    return Math.min(Math.max(h, min), max)
  }

  const isFullyOpen = !isCollapsed && sheetHeight >= maxSheetHeight() - 1

  // Cleanly abandon an in-progress drag without changing height — used both on
  // a normal pointerup and as a safety net if the browser releases pointer
  // capture on its own (lostpointercapture), which some mobile engines do
  // mid-gesture without ever delivering a pointerup. Without this, isDragging
  // could get stuck true (CSS transition stuck off) or worse.
  const abandonDrag = () => {
    dragStateRef.current.active = false
    setIsDragging(false)
  }

  const handleHandlePointerDown = (e) => {
    if (!Number.isFinite(e.clientY)) return
    dragStateRef.current = {
      active: true,
      startY: e.clientY,
      startHeight: sheetHeight,
      moved: false,
      lastY: e.clientY,
      lastT: e.timeStamp,
      velocity: 0,
    }
    setIsDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handleHandlePointerMove = (e) => {
    const d = dragStateRef.current
    if (!d.active || !Number.isFinite(e.clientY)) return
    const delta = d.startY - e.clientY // drag up = taller
    if (Math.abs(delta) > 4) d.moved = true
    const next = clampSheetHeight(d.startHeight + delta)
    setSheetHeight(next)
    setIsCollapsed(next <= getSheetMin() + 4)

    // Rolling velocity estimate (px/ms, +down / -up) for the fling check on release.
    // Heavily smoothed so a single jittery touch sample can't fake a "slam".
    const dt = e.timeStamp - d.lastT
    if (dt > 0) {
      const instant = (e.clientY - d.lastY) / dt
      d.velocity = d.velocity * 0.85 + instant * 0.15
    }
    d.lastY = e.clientY
    d.lastT = e.timeStamp
  }

  const expandSheet = () => {
    setSheetHeight(lastExpandedHeightRef.current || clampSheetHeight(getViewportHeight() * SHEET_DEFAULT_RATIO))
    setIsCollapsed(false)
  }

  const handleHandlePointerUp = () => {
    const d = dragStateRef.current
    const min = getSheetMin()
    if (d.active && !d.moved) {
      // Plain tap (no drag): toggle collapsed <-> last expanded height
      if (isCollapsed) {
        expandSheet()
      } else {
        lastExpandedHeightRef.current = sheetHeight
        setSheetHeight(min)
        setIsCollapsed(true)
      }
    } else if (d.active) {
      // Released mid-drag, moving down fast ("slam it shut"): snap fully closed
      // regardless of how far it had actually been dragged — the transition
      // re-enables the instant isDragging clears below, so this animates
      // smoothly instead of just stopping dead where the finger let go.
      // Upward (or not-fast) releases always lock wherever they are — that's
      // the freeform "pick any height" behavior, only the downward slam is special.
      const distanceDown = d.lastY - d.startY
      if (Number.isFinite(d.velocity) && d.velocity > FLING_VELOCITY && distanceDown > FLING_MIN_DISTANCE) {
        setSheetHeight(min)
        setIsCollapsed(true)
      } else if (!isCollapsed) {
        lastExpandedHeightRef.current = sheetHeight
      }
    }
    abandonDrag()
  }

  // Reorderable section order + per-section collapsed state, persisted.
  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = loadJSON(SECTION_ORDER_KEY, null)
    if (Array.isArray(saved) && saved.length === DEFAULT_SECTION_ORDER.length
      && DEFAULT_SECTION_ORDER.every((id) => saved.includes(id))) {
      return saved
    }
    return DEFAULT_SECTION_ORDER
  })
  useEffect(() => {
    localStorage.setItem(SECTION_ORDER_KEY, JSON.stringify(sectionOrder))
  }, [sectionOrder])

  const [collapsedSections, setCollapsedSections] = useState(() => loadJSON(SECTION_COLLAPSED_KEY, {}))
  useEffect(() => {
    localStorage.setItem(SECTION_COLLAPSED_KEY, JSON.stringify(collapsedSections))
  }, [collapsedSections])
  const toggleSectionCollapsed = (id) => setCollapsedSections((prev) => ({ ...prev, [id]: !prev[id] }))

  const dragSectionRef = useRef(null)
  const [dragOverSection, setDragOverSection] = useState(null)
  const handleSectionDragStart = (id) => (e) => {
    dragSectionRef.current = id
    e.dataTransfer.effectAllowed = 'move'
  }
  const handleSectionDragOver = (id) => (e) => {
    e.preventDefault()
    if (dragSectionRef.current && dragSectionRef.current !== id) setDragOverSection(id)
  }
  const handleSectionDrop = (id) => (e) => {
    e.preventDefault()
    const draggedId = dragSectionRef.current
    setDragOverSection(null)
    dragSectionRef.current = null
    if (!draggedId || draggedId === id) return
    setSectionOrder((prev) => {
      const next = prev.filter((x) => x !== draggedId)
      next.splice(next.indexOf(id), 0, draggedId)
      return next
    })
  }
  const handleSectionDragEnd = () => {
    dragSectionRef.current = null
    setDragOverSection(null)
  }

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showResults, setShowResults] = useState(false)
  const [citiesData, setCitiesData] = useState(null)

  useEffect(() => {
    if (!searchQuery.trim() || citiesData) return
    loadCities().then(setCitiesData).catch(() => {})
  }, [searchQuery, citiesData])

  // Extended region search (shared with the Ctrl+F modal): region / country /
  // ID / zone plus climate, weather, organization and population thresholds.
  const handleSearch = useMemo(
    () => searchRegions(regionsData, searchQuery, { organizationsCatalog, weatherData, citiesData, limit: 10 }),
    [regionsData, searchQuery, organizationsCatalog, weatherData, citiesData]
  )

  // Update search results when search changes
  useMemo(() => {
    setSearchResults(handleSearch)
    setShowResults(searchQuery.trim().length > 0)
  }, [handleSearch, searchQuery])

  const [resultFlags, setResultFlags] = useState({})
  useEffect(() => {
    searchResults.forEach((r) => {
      if (r.country_id && !(r.country_id in resultFlags)) {
        getCountryFlag(r.country_id, (url) => {
          setResultFlags((prev) => ({ ...prev, [r.country_id]: url }))
        })
      }
    })
  }, [searchResults])

  const handleSelectRegion = (region) => {
    if (onCenterMap) {
      onCenterMap(region)
      setSearchQuery('')
      setShowResults(false)
    }
  }

  const handleFileImport = (event) => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result)
        onImportJSON(data)
      } catch {
        showToast('Fichier JSON invalide', 'error')
      }
    }
    reader.readAsText(file)

    // Reset to allow importing the same file twice in a row
    event.target.value = ''
  }

  return (
    <Fragment>
      {/* Redundant always-reachable reopen control, sits under the intersite-navbar's
          round arrow (top-left). Mobile only (CSS), shown only while the sheet is
          collapsed — the bottom handle can be hard to reach under phone browser chrome. */}
      {isCollapsed && (
        <button
          className="sheet-reopen-fab"
          onClick={expandSheet}
          aria-label="Afficher le menu"
        >
          <Icon name="menu" size={20} />
        </button>
      )}

      {/* Desktop-only toggle, independent of .sidebar's own transform so it stays
          put (vertically centered, clear of the intersite-navbar's circle) whether
          the panel is collapsed or not — it used to be a child of .sidebar and slid
          along with it, landing on top of the navbar's button when collapsed. */}
      <button
        className={`collapse-button ${isCollapsed ? 'collapsed' : ''}`}
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <Icon name={isCollapsed ? 'chevronRight' : 'chevronLeft'} size={18} />
      </button>

      <div
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isDragging ? 'dragging' : ''} ${isFullyOpen ? 'full-open' : ''}`}
        style={{ '--sheet-height': `${sheetHeight}px` }}
      >
        <button
        className="sheet-handle"
        onPointerDown={handleHandlePointerDown}
        onPointerMove={handleHandlePointerMove}
        onPointerUp={handleHandlePointerUp}
        onPointerCancel={handleHandlePointerUp}
        onLostPointerCapture={abandonDrag}
        aria-label={isCollapsed ? 'Tirer pour afficher le menu' : 'Tirer pour redimensionner ou masquer le menu'}
        aria-expanded={!isCollapsed}
      >
        <span className="sheet-grip" aria-hidden="true" />
        <span className="sheet-label">{isCollapsed ? 'Menu' : 'Masquer'}</span>
      </button>

      {!isCollapsed && (
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h1>Projet Résurgence</h1>
            <p className="subtitle">Carte Interactive</p>
          </div>

          {/* Search Bar */}
          <div className="sidebar-section">
            <SectionTitle icon="search">Recherche</SectionTitle>
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder="Ville, région, pays ou ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowResults(searchQuery.trim().length > 0)}
              />
              {showResults && searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((result, index) => (
                    <div
                      key={`${result.matchType}-${result.region_id || result.name}-${index}`}
                      className="search-result-item"
                      onClick={() => handleSelectRegion(result)}
                    >
                      <div className="result-type">{result.matchType}</div>
                      <div className="result-text">
                        {result.matchType === 'Ville' ? result.name : `#${result.region_id} - ${result.name}`}
                      </div>
                      {result.country_name && (
                        <div className="result-country">
                          {resultFlags[result.country_id] && (
                            <img src={resultFlags[result.country_id]} alt="" className="country-flag-icon-sm" />
                          )}
                          {countryLabel(result.country_name, result.country_id)}
                        </div>
                      )}
                      {result.geographical_area && (
                        <div className="result-area">{result.geographical_area}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {showResults && searchResults.length === 0 && searchQuery.trim() && (
                <div className="search-results">
                  <div className="search-no-results">Aucun résultat trouvé</div>
                </div>
              )}
            </div>
          </div>

          {(() => {
            const sectionProps = (id) => ({
              id,
              collapsed: !!collapsedSections[id],
              onToggleCollapsed: () => toggleSectionCollapsed(id),
              onDragStart: handleSectionDragStart(id),
              onDragOver: handleSectionDragOver(id),
              onDrop: handleSectionDrop(id),
              onDragEnd: handleSectionDragEnd,
              dragOver: dragOverSection === id,
            })

            const activeOwnDef = OWN_LAYERS.find((def) => layers[def.key])

            const sectionsById = {
              vue: (
                <SidebarSection {...sectionProps('vue')} icon="view" title="Vue">
                  <select
                    className="main-select"
                    value={!!layers.topography ? 'topography' : (satelliteMode === 'satellite' ? 'satellite' : 'none')}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === 'topography') {
                        onSetSatelliteMode('none')
                        onToggleLayer('topography')
                      } else {
                        onSetSatelliteMode(value)
                        if (layers.topography) onToggleLayer(null, false)
                      }
                    }}
                  >
                    <option value="none">Normale</option>
                    <option value="satellite">Satellite</option>
                    <option value="topography">Topographie</option>
                  </select>
                </SidebarSection>
              ),

              visualisation: (
                <SidebarSection {...sectionProps('visualisation')} icon="layers" title="Visualisation">
                  <select
                    className="main-select"
                    value={
                      layers.countries ? 'countries' :
                      layers.playedCountries ? 'playedCountries' :
                      layers.diplomacy ? 'diplomacy' :
                      layers.population ? 'population' :
                      layers.populationDensity ? 'populationDensity' :
                      layers.gdpPerCapita ? 'gdpPerCapita' :
                      layers.climate ? 'climate' :
                      layers.weather ? 'weather' :
                      layers.recentActivity ? 'recentActivity' :
                      layers.hemisphere ? 'hemisphere' :
                      activeOwnDef?.key || 'neutral'
                    }
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === 'neutral') onToggleLayer(null, false)
                      else onToggleLayer(value)
                    }}
                    disabled={!!layers.topography}
                  >
                    <option value="neutral">Carte neutre (gris)</option>
                    <option value="countries">Contrôle territorial</option>
                    <option value="playedCountries">Pays joués</option>
                    {hasOwnedData && <option value="diplomacy">Relations diplomatiques</option>}
                    <option value="population">Population (habitants)</option>
                    <option value="populationDensity">Densité de population (hab/km²)</option>
                    <option value="gdpPerCapita">PIB / habitant</option>
                    <option value="climate">Zones climatiques</option>
                    <option value="weather">Météo en direct</option>
                    <option value="recentActivity">Activité récente (48 h)</option>
                    <option value="hemisphere">Hémisphères</option>
                    {hasOwnedData && (
                      <optgroup label="Mes données régionales">
                        {OWN_LAYERS.map((def) => (
                          <option key={def.key} value={def.key}>{def.label}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {!!layers.topography && (
                    <p className="section-hint">Indisponible en vue Topographie.</p>
                  )}

                  {activeOwnDef && (
                    <div className="own-data-controls">
                      <p className="section-hint">
                        Visible uniquement pour vos régions ; les autres restent en gris.
                      </p>
                      {activeOwnDef.dynamic && (
                        <select
                          className="own-data-select"
                          value={statType?.[activeOwnDef.dynamic] || ''}
                          onChange={(e) => onStatTypeChange(activeOwnDef.dynamic, e.target.value)}
                        >
                          {(activeOwnDef.dynamic === 'infrastructure'
                            ? [...new Set((infraTypes || []).map((t) => t.type))].sort()
                            : statMeta.infra_types
                          ).map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      )}
                      {activeOwnDef.structFilter && (
                        <div className="struct-filter">
                          <select
                            className="own-data-select"
                            value={structFilter?.type || ''}
                            onChange={(e) => onStructFilterChange('type', e.target.value)}
                          >
                            <option value="">Tous les types</option>
                            {structOptions.types.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <select
                            className="own-data-select"
                            value={structFilter?.specialisation || ''}
                            onChange={(e) => onStructFilterChange('specialisation', e.target.value)}
                          >
                            <option value="">Toutes spécialisations</option>
                            {structOptions.specialisations.map((sp) => (
                              <option key={sp} value={sp}>{sp === 'NA' ? 'Sans spécialisation' : sp}</option>
                            ))}
                          </select>
                          {structOptions.levels.length > 0 && (() => {
                            const idx = structFilter?.level
                              ? structOptions.levels.indexOf(Number(structFilter.level)) + 1
                              : 0
                            return (
                              <div className="struct-level-slider">
                                <div className="struct-level-slider-head">
                                  <span>Niveau</span>
                                  <span className="struct-level-value">
                                    {idx === 0 ? 'Tous' : `Nv.${structOptions.levels[idx - 1]}`}
                                  </span>
                                </div>
                                <input
                                  type="range"
                                  min={0}
                                  max={structOptions.levels.length}
                                  step={1}
                                  value={idx}
                                  onChange={(e) => {
                                    const i = parseInt(e.target.value, 10)
                                    onStructFilterChange('level', i === 0 ? '' : structOptions.levels[i - 1])
                                  }}
                                />
                              </div>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {ownedCountryId == null && (
                    <p className="section-hint">
                      <Icon name="lock" size={12} /> <a className="own-data-login-inline" href={buildLoginUrl()}>Connectez-vous</a> pour afficher vos statistiques régionales (logement, électricité, structures, infrastructures).
                    </p>
                  )}
                </SidebarSection>
              ),


              'bulles': (
                <SidebarSection {...sectionProps('bulles')} icon="layers" title="Carte à bulles">
                  <label className="layer-toggle">
                    <input
                      type="checkbox" className="pr-check"
                      checked={!!showBubbles}
                      onChange={onToggleShowBubbles}
                    />
                    <span>Afficher les bulles proportionnelles</span>
                  </label>
                  {showBubbles && (
                    <div className="filter-group">
                      <label>Métrique</label>
                      <select
                        className="main-select"
                        value={bubbleMetric || 'population'}
                        onChange={(e) => onBubbleMetricChange(e.target.value)}
                      >
                        {BUBBLE_METRICS.map((m) => (
                          <option key={m.key} value={m.key}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <p className="section-hint">
                    Cercles proportionnels (∝ √valeur) centrés sur chaque région — complément du choroplèthe.
                  </p>
                </SidebarSection>
              ),

              'elements-rp': (
                <SidebarSection {...sectionProps('elements-rp')} icon="grid" title="Éléments RP">
                  <label className="layer-toggle structures-overlay-toggle">
                    <input
                      type="checkbox" className="pr-check"
                      checked={!!showStructures}
                      onChange={onToggleShowStructures}
                    />
                    <span>Structures et centrales</span>
                  </label>

                  <label className="layer-toggle structures-overlay-toggle">
                    <input
                      type="checkbox" className="pr-check"
                      checked={!!showInfrastructure}
                      onChange={onToggleShowInfrastructure}
                    />
                    <span>Infrastructures</span>
                  </label>

                  {showInfrastructure && (
                    <p className="section-hint">Style des tracés : engrenage ⚙ → Éléments RP.</p>
                  )}

                  <label className="layer-toggle structures-overlay-toggle">
                    <input
                      type="checkbox" className="pr-check"
                      checked={!!showMilitary}
                      onChange={onToggleShowMilitary}
                    />
                    <span>Unités militaires</span>
                  </label>
                </SidebarSection>
              ),

              'monde': (
                <SidebarSection {...sectionProps('monde')} icon="globe" title="Monde réel">
                  <p className="section-hint">Repères géographiques réels. Style : engrenage ⚙.</p>
                  <label className="layer-toggle structures-overlay-toggle">
                    <input type="checkbox" className="pr-check" checked={!!showCities} onChange={onToggleShowCities} />
                    <span>Villes</span>
                  </label>
                  <label className="layer-toggle structures-overlay-toggle">
                    <input type="checkbox" className="pr-check" checked={!!showRoads} onChange={onToggleShowRoads} />
                    <span>Routes & autoroutes</span>
                  </label>
                  <label className="layer-toggle structures-overlay-toggle">
                    <input type="checkbox" className="pr-check" checked={!!showRailways} onChange={onToggleShowRailways} />
                    <span>Voies ferrées</span>
                  </label>
                  <label className="layer-toggle structures-overlay-toggle">
                    <input type="checkbox" className="pr-check" checked={!!showHydrography} onChange={onToggleShowHydrography} />
                    <span>Fleuves, rivières & lacs</span>
                  </label>
                </SidebarSection>
              ),


              // « Guerres en cours » — le pendant conflictuel des organisations.
              // Une section vide et une section en échec ne doivent pas se
              // ressembler : `wars === null` signifie « je n'ai pas pu charger »,
              // et le dire vaut mieux que laisser croire qu'il n'y a pas de
              // guerre. C'est la même règle que le bandeau de carte périmée.
              guerres: (
                <SidebarSection {...sectionProps('guerres')} icon="alert" title="Visualisation — Guerres en cours">
                  {wars === null ? (
                    <p className="section-hint">
                      Les guerres n'ont pas pu être chargées. Cette section ne dit
                      pas qu'il n'y en a aucune — elle dit qu'elle ne sait pas.
                    </p>
                  ) : (wars || []).length === 0 ? (
                    <p className="section-hint">Aucune guerre en cours.</p>
                  ) : (
                    <>
                      <p className="section-hint">
                        Cliquez une guerre pour voir les deux camps sur la carte.
                      </p>
                      <div className="war-list">
                        {wars.map((war) => {
                          const active = selectedWarId === war.id
                          return (
                            <button
                              key={war.id}
                              className={`war-card ${active ? 'active' : ''}`}
                              onClick={() => onSelectWar(war.id)}
                              title={`Déclarée le ${war.declaredOn || '—'}`}
                            >
                              <span className="war-side attacker">{war.attackerName}</span>
                              <span className="war-vs">⚔</span>
                              <span className="war-side defender">{war.defenderName || '—'}</span>
                              <span className="war-date">{(war.declaredOn || '').slice(0, 10)}</span>
                            </button>
                          )
                        })}
                      </div>
                    </>
                  )}
                </SidebarSection>
              ),

              ...(organizationsCatalog && Object.keys(organizationsCatalog).length > 0 ? {
                organisations: (
                  <SidebarSection {...sectionProps('organisations')} icon="grid" title="Visualisation — Organisations">
                    <p className="section-hint">Cliquez une organisation pour voir ses membres sur la carte.</p>
                    <div className="org-card-grid">
                      {Object.values(organizationsCatalog).map((org) => {
                        const active = selectedOrgId === org.org_id
                        return (
                          <button
                            key={org.org_id}
                            className={`org-card ${active ? 'active' : ''}`}
                            style={active ? { borderColor: org.color, boxShadow: `0 0 0 1px ${org.color}` } : undefined}
                            onClick={() => onSelectOrg(org.org_id)}
                            title={org.org_type}
                          >
                            <span className="org-card-swatch" style={{ backgroundColor: org.color }} />
                            <span className="org-card-emoji">{org.emoji}</span>
                            <span className="org-card-name">{org.name}</span>
                          </button>
                        )
                      })}
                    </div>
                  </SidebarSection>
                ),
              } : {}),

              projection: (
                <SidebarSection {...sectionProps('projection')} icon="globe" title="Projection">
                  <button
                    className={`projection-toggle ${isGlobeView ? 'globe-active' : ''}`}
                    onClick={onToggleGlobeView}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: isGlobeView ? 'var(--accent-color)' : 'var(--background-light)',
                      color: isGlobeView ? 'white' : 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.95em',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Icon name="globe" size={16} />
                    {isGlobeView ? 'Vue Globe' : 'Vue Plane'}
                  </button>
                </SidebarSection>
              ),

              filtres: (
                <SidebarSection
                  {...sectionProps('filtres')}
                  icon="filter"
                  title="Filtres"
                  extra={(
                    <button
                      className="reset-filters-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        onUpdateFilter('hemisphere', null)
                        onUpdateFilter('continent', null)
                        onUpdateFilter('minPopulation', 0)
                        onUpdateFilter('maxPopulation', 10000000)
                      }}
                    >
                      Réinitialiser
                    </button>
                  )}
                >
                  <div className="filter-group">
                    <label>Hémisphère</label>
                    <select
                      value={filters.hemisphere || ''}
                      onChange={(e) => onUpdateFilter('hemisphere', e.target.value || null)}
                    >
                      <option value="">Tous</option>
                      <option value="north">Hémisphère Nord</option>
                      <option value="south">Hémisphère Sud</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Continent</label>
                    <select
                      value={filters.continent || ''}
                      onChange={(e) => onUpdateFilter('continent', e.target.value || null)}
                    >
                      <option value="">Tous</option>
                      <option value="Afrique">Afrique</option>
                      <option value="Amerique du Nord">Amérique du Nord</option>
                      <option value="Amerique du Sud">Amérique du Sud</option>
                      <option value="Asie">Asie</option>
                      <option value="Europe">Europe</option>
                      <option value="Oceanie">Océanie</option>
                      <option value="Antarctique">Antarctique</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Population minimum</label>
                    <input
                      type="range"
                      min="0"
                      max="10000000"
                      step="100000"
                      value={filters.minPopulation}
                      onChange={(e) => onUpdateFilter('minPopulation', parseInt(e.target.value))}
                    />
                    <span className="range-value">
                      {filters.minPopulation.toLocaleString('fr-FR')}
                    </span>
                  </div>
                </SidebarSection>
              ),

              stats: (
                <SidebarSection {...sectionProps('stats')} icon="info" title="Statistiques">
                  <div className="stats">
                    <div className="stat-item">
                      <span className="stat-label">Régions totales</span>
                      <span className="stat-value">
                        {regionsData?.features?.length
                          ? regionsData.features.length.toLocaleString('fr-FR')
                          : '—'}
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Pays actifs</span>
                      <span className="stat-value">{activeCountriesCount || '—'}</span>
                    </div>
                  </div>
                </SidebarSection>
              ),
            }

            return sectionOrder
              .filter((id) => sectionsById[id] && !hiddenSectionSet.has(id))
              .map((id) => <Fragment key={id}>{sectionsById[id]}</Fragment>)
          })()}

          <div className="sidebar-section plan-mode-section">
            <SectionTitle icon="crosshair">Règle</SectionTitle>

            <button
              className={`plan-mode-toggle ${rulerMode ? 'active' : ''}`}
              onClick={onToggleRulerMode}
            >
              {rulerMode && <Icon name="check" size={14} />}
              {rulerMode ? 'Mode actif' : 'Activer le mode'}
            </button>

            {rulerMode && (
              <>
                <div className="plan-mode-help">
                  <p><Icon name="info" size={13} /> <strong>Cliquez</strong> pour poser des points, <strong>glissez</strong>-en un pour le déplacer, <strong>clic droit</strong> pour le supprimer.</p>
                </div>

                <div className="selection-mode">
                  <label>Distance totale</label>
                  <div className="mode-buttons">
                    <button
                      className={!rulerCrowFlies ? 'active' : ''}
                      onClick={() => onRulerCrowFliesChange(false)}
                    >
                      Trajet
                    </button>
                    <button
                      className={rulerCrowFlies ? 'active' : ''}
                      onClick={() => onRulerCrowFliesChange(true)}
                    >
                      Vol d'oiseau
                    </button>
                  </div>
                </div>

                {rulerPath.length > 0 && (() => {
                  const coords = rulerPath.map((p) => [p.lngLat.lng, p.lngLat.lat])
                  const lengthKm = rulerCrowFlies && coords.length >= 2
                    ? pathLengthKm([coords[0], coords[coords.length - 1]])
                    : pathLengthKm(coords)
                  return (
                    <div className="plan-stats">
                      <span className="stat-label">{rulerPath.length} point(s)</span>
                      <span className="stat-value">{lengthKm.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km</span>
                    </div>
                  )
                })()}

                <div className="plan-actions">
                  <button onClick={onUndoRulerPoint} className="action-btn export" disabled={rulerPath.length === 0}>
                    <Icon name="undo" size={14} /> Dernier point
                  </button>
                  <button onClick={onClearRuler} className="action-btn clear" disabled={rulerPath.length === 0}>
                    <Icon name="trash" size={14} /> Effacer
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="sidebar-section plan-mode-section">
            <SectionTitle icon="radio">Cercle de portée</SectionTitle>
            <button
              className={`plan-mode-toggle ${rangeMode ? 'active' : ''}`}
              onClick={onToggleRangeMode}
            >
              {rangeMode && <Icon name="check" size={14} />}
              {rangeMode ? 'Choisir le centre' : 'Activer le mode'}
            </button>
            {rangeMode && (
              <>
                <div className="plan-mode-help">
                  <p><Icon name="info" size={13} /> Saisissez une portée, puis cliquez son centre sur la carte.</p>
                </div>
                <div className="filter-group">
                  <label htmlFor="range-radius-km">Rayon en kilomètres</label>
                  <input
                    id="range-radius-km"
                    type="number"
                    min="1"
                    max="20000"
                    step="10"
                    value={rangeRadiusKm}
                    onChange={(event) => onRangeRadiusChange(Math.min(20000, Math.max(1, Number(event.target.value) || 1)))}
                  />
                </div>
                <div className="plan-stats">
                  <span className="stat-label">Centre</span>
                  <span className="stat-value">{rangeCenter ? `${rangeCenter.lng.toFixed(2)}, ${rangeCenter.lat.toFixed(2)}` : 'À placer'}</span>
                </div>
                <div className="plan-actions">
                  <button onClick={onClearRange} className="action-btn clear" disabled={!rangeCenter}>
                    <Icon name="trash" size={14} /> Effacer
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="sidebar-section plan-mode-section">
            <SectionTitle icon="palette">Mode Planification</SectionTitle>

            <button
              className={`plan-mode-toggle ${planMode ? 'active' : ''}`}
              onClick={onTogglePlanMode}
            >
              {planMode && <Icon name="check" size={14} />}
              {planMode ? 'Mode actif' : 'Activer le mode'}
            </button>

            {planMode && (
              <>
                <div className="plan-mode-help">
                  <p><Icon name="info" size={13} /> <strong>Cliquez</strong> pour colorier, <strong>recliquez</strong> sur une région coloriée pour l'effacer (ou clic droit)</p>
                </div>

                <div className="color-picker-container">
                  <label htmlFor="color-picker">Couleur sélectionnée:</label>
                  <input 
                    id="color-picker"
                    type="color" 
                    value={selectedColor}
                    onChange={(e) => onColorChange(e.target.value)}
                    className="color-picker"
                  />
                  <span className="color-value">{selectedColor}</span>
                </div>

                <div className="selection-mode">
                  <label>Mode de sélection:</label>
                  <div className="mode-buttons">
                    <button 
                      className={selectionMode === 'single' ? 'active' : ''}
                      onClick={() => onSelectionModeChange('single')}
                    >
                      Simple
                    </button>
                    <button 
                      className={selectionMode === 'area' ? 'active' : ''}
                      onClick={() => onSelectionModeChange('area')}
                    >
                      Zone
                    </button>
                  </div>
                </div>

                <div className="plan-stats">
                  <span className="stat-label">Régions colorées:</span>
                  <span className="stat-value">{coloredRegionsCount || 0}</span>
                </div>

                <div className="plan-actions">
                  <button onClick={onClearAll} className="action-btn clear">
                    <Icon name="trash" size={14} /> Effacer tout
                  </button>
                  <button onClick={onExportPNG} className="action-btn export">
                    <Icon name="image" size={14} /> PNG
                  </button>
                  <button onClick={onExportJSON} className="action-btn export">
                    <Icon name="save" size={14} /> Sauvegarder
                  </button>
                  <label className="action-btn import">
                    <Icon name="folderOpen" size={14} /> Charger
                    <input
                      type="file"
                      accept=".json,application/json"
                      onChange={handleFileImport}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </>
            )}
          </div>

          <div className="sidebar-section plan-mode-section">
            <SectionTitle icon="swords">Planification militaire</SectionTitle>

            {ownedCountryId == null ? (
              <p className="own-data-hint">
                Connectez-vous pour créer des plans de guerre (dessins, flèches,
                symboles, calques, placement d'unités).
              </p>
            ) : (
              <>
                <button
                  className={`plan-mode-toggle ${militaryPlanning ? 'active' : ''}`}
                  onClick={onToggleMilitaryPlanning}
                >
                  {militaryPlanning && <Icon name="check" size={14} />}
                  {militaryPlanning ? 'Mode actif' : 'Activer le mode'}
                </button>
                {militaryPlanning && !showMilitary && (
                  <div className="plan-mode-help">
                    <p><Icon name="info" size={13} /> Active aussi le calque « Unités militaires » pour voir tes déploiements.</p>
                  </div>
                )}
              </>
            )}
          </div>

          {(showMilitary || militaryPlanning) && military && (
            <div className="sidebar-section military-section">
              <SectionTitle icon="shield">Commandement</SectionTitle>
              <MilitaryPanel {...military} />
            </div>
          )}

          <div className="sidebar-section construction-mode-section">
            <SectionTitle icon="building">Mode Construction</SectionTitle>

            {ownedCountryId == null ? (
              <p className="own-data-hint">
                Connectez-vous pour construire des structures et infrastructures sur vos régions.
              </p>
            ) : (
              <>
                <button
                  className={`plan-mode-toggle ${constructionMode ? 'active' : ''}`}
                  onClick={onToggleConstructionMode}
                >
                  {constructionMode && <Icon name="check" size={14} />}
                  {constructionMode ? 'Mode actif' : 'Activer le mode'}
                </button>

                {constructionMode && (
                  <>
                    <div className="selection-mode">
                      <div className="mode-buttons">
                        <button
                          className={constructionTool === 'structure' ? 'active' : ''}
                          onClick={() => onConstructionToolChange('structure')}
                        >
                          Structures
                        </button>
                        <button
                          className={constructionTool === 'infra' ? 'active' : ''}
                          onClick={() => onConstructionToolChange('infra')}
                        >
                          Infrastructures
                        </button>
                      </div>
                    </div>

                    {constructionTool === 'structure' && (
                      <>
                        <div className="plan-mode-help">
                          <p><Icon name="info" size={13} /> Choisissez un type de structure puis <strong>cliquez</strong> sur une de vos régions pour la construire. <strong>Glissez</strong> une structure existante pour la déplacer (dans sa région).</p>
                        </div>

                        <div className="filter-group">
                          <label>Type de structure</label>
                          <select
                            value={constructionSelection.type}
                            onChange={(e) => onConstructionSelectionChange('type', e.target.value)}
                          >
                            <option value="">Choisir un type…</option>
                            {/* Logement is forced in: it is buildable through the
                                housing tables, not through structures_datas. */}
                            {[...new Set([...structureTypeData.map((d) => d.type), 'Logement', 'Centrale électrique'])].sort().map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>

                        {/* Housing is not levelled — it is a density × style ×
                            quality × capacity combination, exactly like MARC's
                            /construct_housing. The structures_datas "Logement
                            Nv.N" rows are a legacy artefact and are ignored. */}
                        {constructionSelection.type === 'Logement' && (
                          <>
                            <div className="filter-group">
                              <label>Densité</label>
                              <select
                                value={constructionSelection.density_id ?? ''}
                                onChange={(e) => onConstructionSelectionChange('density_id', parseInt(e.target.value, 10))}
                              >
                                <option value="">Choisir…</option>
                                {(housingOptions?.densities || []).map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {d.name} — {d.base_cost_per_person.toLocaleString('fr-FR')} crédits/hab. — confort {d.comfort_points >= 0 ? '+' : ''}{d.comfort_points}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="filter-group">
                              <label>Style architectural</label>
                              <select
                                value={constructionSelection.style_id ?? ''}
                                onChange={(e) => onConstructionSelectionChange('style_id', parseInt(e.target.value, 10))}
                              >
                                <option value="">Choisir…</option>
                                {(housingOptions?.styles || []).map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.name} — ×{s.price_multiplier} — confort {s.comfort_points >= 0 ? '+' : ''}{s.comfort_points}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="filter-group">
                              <label>Qualité de construction</label>
                              <select
                                value={constructionSelection.quality_id ?? ''}
                                onChange={(e) => onConstructionSelectionChange('quality_id', parseInt(e.target.value, 10))}
                              >
                                <option value="">Choisir…</option>
                                {(housingOptions?.qualities || []).map((q) => (
                                  <option key={q.id} value={q.id}>
                                    {q.name} — ×{q.price_multiplier} — confort {q.comfort_points >= 0 ? '+' : ''}{q.comfort_points}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="filter-group">
                              <label>Capacité (habitants)</label>
                              <input
                                type="number"
                                min="1"
                                step="100"
                                value={constructionSelection.capacity ?? ''}
                                onChange={(e) => onConstructionSelectionChange('capacity', parseInt(e.target.value, 10) || 0)}
                              />
                            </div>

                            {(() => {
                              const cost = housingCost(housingOptions, constructionSelection)
                              const comfort = housingComfort(housingOptions, constructionSelection)
                              if (cost === null || comfort === null) return null
                              return (
                                <div className="infra-path-info">
                                  <span className="infra-cost-preview">
                                    ≈ {cost.toLocaleString('fr-FR')} crédits — confort {comfort}/100
                                  </span>
                                </div>
                              )
                            })()}
                          </>
                        )}

                        {constructionSelection.type === 'Centrale électrique' && (
                          <>
                            <div className="filter-group">
                              <label>Type de centrale</label>
                              <select value={constructionSelection.power_type || ''} onChange={(e) => onConstructionSelectionChange('power_type', e.target.value)}>
                                <option value="">Choisir…</option>
                                {[...new Set((powerPlantData || []).map((p) => p.type))].sort().map((type) => <option key={type} value={type}>{type}</option>)}
                              </select>
                            </div>
                            {constructionSelection.power_type && (
                              <div className="filter-group">
                                <label>Niveau</label>
                                <select value={constructionSelection.level ?? ''} onChange={(e) => onConstructionSelectionChange('level', parseInt(e.target.value, 10))}>
                                  <option value="">Choisir…</option>
                                  {(powerPlantData || []).filter((p) => p.type === constructionSelection.power_type).sort((a, b) => a.level - b.level).map((p) => (
                                    <option key={p.level} value={p.level}>Nv.{p.level} — {p.production_mwh.toLocaleString('fr-FR')} MWh — {p.construction_cost.toLocaleString('fr-FR')} crédits</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        )}

                        {constructionSelection.type && constructionSelection.type !== 'Logement' && constructionSelection.type !== 'Centrale électrique' && (() => {
                          const specialisations = [...new Set(
                            structureTypeData
                              .filter((d) => d.type === constructionSelection.type)
                              .map((d) => d.specialisation)
                          )]
                          if (specialisations.length <= 1) return null
                          return (
                          <div className="filter-group">
                            <label>Spécialisation</label>
                            <select
                              value={constructionSelection.specialisation}
                              onChange={(e) => onConstructionSelectionChange('specialisation', e.target.value)}
                            >
                              <option value="">Choisir…</option>
                              {specialisations.sort().map((sp) => (
                                <option key={sp} value={sp}>{sp === 'NA' ? 'Sans spécialisation' : sp}</option>
                              ))}
                            </select>
                          </div>
                          )
                        })()}

                        {constructionSelection.type && constructionSelection.type !== 'Logement' && constructionSelection.type !== 'Centrale électrique' && constructionSelection.specialisation && (
                          <div className="filter-group">
                            <label>Niveau</label>
                            <select
                              value={constructionSelection.level ?? ''}
                              onChange={(e) => onConstructionSelectionChange('level', parseInt(e.target.value, 10))}
                            >
                              <option value="">Choisir…</option>
                              {structureTypeData
                                .filter((d) => d.type === constructionSelection.type && d.specialisation === constructionSelection.specialisation)
                                .sort((a, b) => a.level - b.level)
                                .map((d) => (
                                  <option key={d.level} value={d.level}>
                                    Nv.{d.level} — capacité {d.capacity.toLocaleString('fr-FR')} — {d.cout_construction.toLocaleString('fr-FR')} crédits
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}

                        <label className="layer-toggle">
                          <input
                            type="checkbox" className="pr-check"
                            checked={!!skipConstructionConfirm}
                            onChange={onToggleSkipConstructionConfirm}
                          />
                          <span>Passer la confirmation</span>
                        </label>
                      </>
                    )}

                    {constructionTool === 'infra' && (
                      <InfraToolPanel
                        infraMode={infraMode}
                        onInfraModeChange={onInfraModeChange}
                        infraTypes={infraTypes}
                        statInfraTypes={statMeta.infra_types}
                        infrastructureSelection={infrastructureSelection}
                        onInfrastructureSelectionChange={onInfrastructureSelectionChange}
                        infrastructureData={infrastructureData}
                        selectedTroncIds={selectedTroncIds}
                        onTroncSelect={onTroncSelect}
                        onTroncClearSelection={onTroncClearSelection}
                        onTroncRework={onTroncRework}
                        onTroncReset={onTroncReset}
                        onTroncMerge={onTroncMerge}
                        infrastructurePath={infrastructurePath}
                        editingInfra={editingInfra}
                        onFinishInfrastructure={onFinishInfrastructure}
                        onUndoInfraPoint={onUndoInfraPoint}
                        onRestartInfrastructure={onRestartInfrastructure}
                        onCancelInfrastructure={onCancelInfrastructure}
                      />
                    )}

                    {/* Applies to both tools: every build is announced in the
                        country's public channel, or its secret one when ticked.
                        The choice is remembered across sessions. */}
                    <label
                      className={`layer-toggle ${hasSecretChannel ? '' : 'disabled'}`}
                      title={hasSecretChannel
                        ? 'Annoncer la construction dans le salon secret plutôt que le salon public'
                        : 'Aucun salon secret configuré pour votre pays'}
                    >
                      <input
                        type="checkbox" className="pr-check"
                        checked={!!notifySecret && !!hasSecretChannel}
                        disabled={!hasSecretChannel}
                        onChange={onToggleNotifySecret}
                      />
                      <span>Annoncer dans le salon secret</span>
                    </label>

                    {constructionStatus?.state !== 'idle' && (
                      <div className={`construction-status ${constructionStatus?.state}`}>
                        {constructionStatus?.message}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="sidebar-footer">
            <a href="https://discord.projet-resurgence.fr/" target="_blank" rel="noopener noreferrer">
              Rejoindre le Discord
            </a>
            <a href="https://projet-resurgence.fr" target="_blank" rel="noopener noreferrer">
              Site officiel
            </a>
          </div>
        </div>
      )}
      </div>
    </Fragment>
  )
}

export default Sidebar
